import { useContext, useState, useEffect, useRef, useCallback } from 'react'
import { ReactSortable } from 'react-sortablejs'

import ActionsDropdown from '../../UI/ActionsDropdown'
import { ResizableExternalDocumentColumn, ExternalDocumentsRow, ExternalDocumentsActions, ColumnsFilter, SharedDocsCommentsModal } from '../'
import { GlobalContext, TeamContext, ColumnsContext, SharedDocumentsContext } from '../../../context'
import { perPageOptions } from '../../../constants'
import { isCookieAccepted, saveTeamPaginationDataToLS, getTeamColumnsOrderData, saveTeamColumnsOrderDataToLS, removeTeamColumnsOrderData, getTeamPaginationData, getTeamColumnsData, saveTeamSharedDocumentsColumnWidthsToLS } from '../../../utils'

const ExternalDocumentsView = ({ filteredDocs, paginatedData, refreshDocs, ready, setPaginatedData, onSort, selectedDocuments, allChecked, onAllCheckedChange, onSetSelectedDocuments, onSetAllChecked }) => {
  const { t } = useContext(GlobalContext)
  const { selectedTeam } = useContext(TeamContext)
  const { sharedDocsTableWidth, setSharedDocsTableWidth, sharedDocsTableColumnsWidths, setSharedDocsTableColumnsWidths } = useContext(ColumnsContext)
  const { setSharedDocumentsSelectedFilters } = useContext(SharedDocumentsContext)
  const [defaultFields, setDefaultFields] = useState([])
  const [columns, setColumns] = useState([
    { label: t('general.name'), value: 'name', icon: 'text_fields', checked: true },
    { label: t('dashboard.created_on'), value: 'created', icon: 'description', checked: true },
    { label: t('dashboard.updated_on'), value: 'updated_at', icon: 'person', checked: true },
    { label: 'Users', value: 'users', icon: 'group', checked: true },
  ])
  const [search, setSearch] = useState('')
  const [perPage, setPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [allPages, setAllPages] = useState(1)
  const [paginationReady, setPaginationReady] = useState(false)
  const [activeResizableColumn, setActiveResizableColumn] = useState(null)
  const [columnsUpdated, setColumnsUpdated] = useState(false)
  const [tableWidthReady, setTableWidthReady] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [sharedDocId, setSharedDocId] = useState(null)
  const [sharedDoc, setSharedDoc] = useState(null)
  const colRefs = useRef([])

  // Check if pagination data is saved in LS
  useEffect(() => {
    const paginationData = getTeamPaginationData()
    if(selectedTeam && paginationData && paginationData[selectedTeam.id] && paginationData[selectedTeam.id].shared_documents) {
      const docsData = paginationData[selectedTeam.id].shared_documents
      let all = Math.ceil(filteredDocs.length / docsData.items || 10)
      docsData.items && setPerPage(docsData.items)
      docsData.current && setCurrentPage(docsData.current <= all ? docsData.current : 1)
      docsData.all && setAllPages(docsData.all)
    }
  }, [filteredDocs, selectedTeam])

  // order columns...
  useEffect(() => {
    if(selectedTeam && !columnsUpdated) {
      let shouldUpdate = false
      let newColumns = [...columns]
      setDefaultFields([...columns, { label: '', value: 'helper', icon: '', checked: true }])

      // Show only checked columns
      const teamColData = getTeamColumnsData()
      if(teamColData && teamColData[selectedTeam.id]) {
        const sharedDocsColsValues = teamColData[selectedTeam.id].shared_documents
        let arr = []
        if(sharedDocsColsValues) {
          arr = newColumns.map(c => sharedDocsColsValues.includes(c.value) ? {...c, checked: true} : {...c, checked: c.value !== 'name' ? false : true})
          newColumns = arr
        }
      }

      // Change order
      const colOrderValues = getTeamColumnsOrderData()
      if(colOrderValues && colOrderValues[selectedTeam.id]) {
        const collectionColOrderValues = colOrderValues[selectedTeam.id].shared_documents 
        if(collectionColOrderValues) {
          let orderedCols = [] 
          collectionColOrderValues.forEach(val => {
            const col = newColumns.find(c => c.value === val)
            col && orderedCols.push(col)
          })
          // Check if there are some columns that were not saved to columns order array, if so add them 
          const filterHiddenValues = [] 
          newColumns.forEach(c => {
            if(!collectionColOrderValues.includes(c.value)) {
              filterHiddenValues.push(c)
            }
          })
          if(filterHiddenValues.length > 0) {
            orderedCols.push(...filterHiddenValues)
          }
          newColumns = orderedCols
          shouldUpdate = true
        }
      }

      // Add helper column
      newColumns.push({ label: '', value: 'helper', icon: '', checked: true })
      shouldUpdate = true

      // Update only if new columns are added or order changed
      if(shouldUpdate) {
        setColumns(newColumns)
      }
      setColumnsUpdated(true)
    }
  }, [selectedTeam, columnsUpdated, columns, t])

  // Calculate table width
  const calculateTableWidth = useCallback(() => {
    let width = 0
    let columnsSaved = false
    let savedColumns = []
    const carbonColumns = getTeamColumnsData()
    if(carbonColumns && carbonColumns[selectedTeam?.id]) {
      const cols = carbonColumns[selectedTeam?.id].shared_documents
      if(cols) {
        columnsSaved = true
        savedColumns = cols
      }
    }
    
    columns.forEach(col => {
      if(col.value !== 'helper') {
        if(columnsSaved) {
          if(savedColumns.includes(col.value)) {
            width += sharedDocsTableColumnsWidths[col.value] || 200
          }
        }else {
          if(col.checked) {
            width += sharedDocsTableColumnsWidths[col.value] || 200
          }
        }
      }
    })
    setSharedDocsTableWidth(width)
  }, [columns, selectedTeam, setSharedDocsTableWidth, sharedDocsTableColumnsWidths])

  // Set width to each column and calculate table width
  useEffect(() => {
    if(!tableWidthReady) {
      calculateTableWidth()
      setTableWidthReady(true)
    }
  }, [calculateTableWidth, tableWidthReady])

  // Change paginated data arr when filtered arr changes
  useEffect(() => {
    let current = currentPage
    let sliceFrom = current === 1 ? 0 : (current - 1) * perPage
    let slicedData
    if(sliceFrom < filteredDocs.length) {
      slicedData = filteredDocs.slice(sliceFrom, sliceFrom + perPage)
    }else {
      slicedData = filteredDocs.slice(0, perPage)
    }
    setPaginatedData(slicedData)
    let all = Math.ceil(filteredDocs.length / perPage)
    setAllPages(all === 0 ? 1 : all)
    setTimeout(() => setPaginationReady(true), 200)
  }, [filteredDocs, perPage, currentPage, allPages, setPaginatedData])

  // On search change
  const handleSearchChange = (e) => {
    setSearch(e.target.value)
    setSharedDocumentsSelectedFilters(prev => ({...prev, search: e.target.value}))
    onSetSelectedDocuments([])
  }

  // On prev button click 
  const handlePrevButtonClick = (e) => {
    e.preventDefault()
    if(currentPage === 1) return 
    setCurrentPage(currentPage - 1)
    onSetAllChecked(false)
    onSetSelectedDocuments([])
    if(isCookieAccepted()) {
      saveTeamPaginationDataToLS(selectedTeam?.id, perPage, currentPage - 1, allPages, 'shared_documents')
    }
  }

  // On next button click 
  const handleNextButtonClick = (e) => {
    e.preventDefault()
    if(currentPage === allPages) return 
    setCurrentPage(currentPage + 1)
    onSetAllChecked(false)
    onSetSelectedDocuments([])
    if(isCookieAccepted()) {
      saveTeamPaginationDataToLS(selectedTeam?.id, perPage, currentPage + 1, allPages, 'shared_documents')
    }
  }

  // On go to specific page
  const handleGoToPage = (e, page) => {
    e.preventDefault()
    if(currentPage === page) return 
    setCurrentPage(page)
    onSetAllChecked(false)
    onSetSelectedDocuments([])
    if(isCookieAccepted()) {
      saveTeamPaginationDataToLS(selectedTeam?.id, perPage, page, allPages, 'shared_documents')
    }
  }

  // Per page change
  const handlePerPageChange = (e, num) => {
    e.preventDefault()
    setPerPage(num)
    onSetAllChecked(false)
    onSetSelectedDocuments([])
    if(Math.ceil(filteredDocs.length / num) < currentPage) {
      setCurrentPage(1)
    }
    window.document.body.click()
    if(isCookieAccepted()) {
      saveTeamPaginationDataToLS(selectedTeam?.id, num, currentPage, allPages, 'shared_documents')
    }
  }

  // Save columns order
  const handleSaveColumnsOrder = useCallback(() => {
    const colOrderValues = getTeamColumnsOrderData()
    if(isCookieAccepted()) {
      let arr = columns.map(f => f.value)
      saveTeamColumnsOrderDataToLS(selectedTeam?.id, arr, 'shared_documents')
    }else {
      if(colOrderValues) {
        removeTeamColumnsOrderData()
      } 
    }
  }, [columns, selectedTeam])

  // On mouse down - set active resizable column
  const handleMouseDown = useCallback((idx, val) => {
    setActiveResizableColumn({ index: idx, value: val })
  }, [])

  // On mouse move - only when there is active resizable column
  const handleMouseMove = useCallback((e) => {
    if(selectedTeam && activeResizableColumn !== null) {
      const el = colRefs.current[activeResizableColumn.index]
      if(!el) return
      const rect = el.getBoundingClientRect()
      const width = e.clientX - rect.x

      if(width > 200) {
        el.style.width = width + 'px'
        let newWidths
        newWidths = {...sharedDocsTableColumnsWidths}
        newWidths[activeResizableColumn.value] = width
        setSharedDocsTableColumnsWidths(newWidths)

        let totalWidth = 0
        columns.forEach(col => {
          if(col.value !== 'helper') {
            totalWidth += newWidths[col.value] || 200
          }
        })
        setSharedDocsTableWidth(totalWidth)
        if(isCookieAccepted()) {
          saveTeamSharedDocumentsColumnWidthsToLS(selectedTeam.id, newWidths)
        }
      }
    }
    // eslint-disable-next-line
  }, [activeResizableColumn, columns, selectedTeam])

  // Add mouse event listeners
  useEffect(() => {
    const handleMouseUp = () => {
      setActiveResizableColumn(null)
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
    }

    if (activeResizableColumn !== null) {
      window.addEventListener("mousemove", handleMouseMove)
      window.addEventListener("mouseup", handleMouseUp)
    }
  }, [activeResizableColumn, handleMouseMove])

  // On shared doc name click
  const handleSharedDocView = useCallback((doc) => {
    setShowPreviewModal(true)
    setSharedDoc(doc)
    setSharedDocId(doc.id)
  }, [])

  // On close preview modal
  const handleClosePreviewModal = () => {
    setShowPreviewModal(false)
    setSharedDoc(null)
    setSharedDocId(null)
  }

  return (
    <div className="list-view-v2">
      <div className="list-view-v2__head">
        <div className="search">
          <div className="icon">
            <span className="material-symbols-outlined weight-300">search</span>
          </div>
          <input type="text" value={search} onChange={handleSearchChange} />
        </div>
        <div className="per-page">
          <ActionsDropdown
            trigger={<a href="#/" className="text-style-4">{perPage} {t('general.per_page')} <span className="material-symbols-outlined">expand_more</span></a>}
            hideHeader
            dropdownClass="pagination-dropdown"
            width={120}
          >
            <ul>
              {perPageOptions.map((num, i) => (
                <li key={i}>
                  <div className="link">
                    <a href="/#" onClick={(e) => handlePerPageChange(e, num)}>{num}</a>
                  </div>
                </li>
              ))}
            </ul>
          </ActionsDropdown>
        </div>
        <div className="filter">
          <ColumnsFilter 
            columns={columns} 
            setColumns={setColumns} 
            onOrderSave={handleSaveColumnsOrder} 
            view="shared_documents" 
            defaultFields={defaultFields}
            calculateTableWidth={calculateTableWidth}
          />
        </div>
      </div>

      <div className="list-view-v2__body">
        <div className="list-view-table-wrapper u-custom-scrollbar u-custom-scrollbar--3">
          {selectedDocuments.length > 0 && (
            <ExternalDocumentsActions allChecked={allChecked} onAllCheckedChange={onAllCheckedChange} selectedItems={selectedDocuments} setSelectedItems={onSetSelectedDocuments} />
          )}
          <div className="list-view-table" style={{minWidth: sharedDocsTableWidth}}>
            <div className="list-view-table__head">
              <ReactSortable 
                list={columns} 
                setList={setColumns} 
                animation={200} 
                delayOnTouchStart={true} 
                delay={2} 
                dragClass="dragged-field--2"
                ghostClass="selected-field"
                filter=".filtered-out"
                draggable=".draggable-item"
                onEnd={handleSaveColumnsOrder}
                handle=".sort"      
                className="sortable-row-items"  
              >
                {columns.filter(c => c.checked).map((column, idx) => {
                  return (
                    <ResizableExternalDocumentColumn column={column} idx={idx} onMouseDown={handleMouseDown} onSort={onSort} ref={(item) => colRefs.current[idx] = item} key={idx} />
                  )
                })}
              </ReactSortable>
              <div className="list-view-table__cell checkbox">
                {!allChecked 
                  ? <span className="material-symbols-outlined" onClick={onAllCheckedChange}>check_box_outline_blank</span>
                  : <span className="material-symbols-outlined filled" onClick={onAllCheckedChange}>check_box</span>
                }
              </div>
            </div>
            <div className="list-view-table__body">
              {(!ready || !paginationReady) && (
                <div className="loader-wrapper"><div className="loader-v2"></div></div>
              )}
              {ready && paginationReady && paginatedData.map((doc, idx) => {

                return (
                  <ExternalDocumentsRow 
                    key={idx} 
                    doc={doc}
                    id={doc.id}
                    selectedDocuments={selectedDocuments}
                    onSetSelectedDocuments={onSetSelectedDocuments}
                    allChecked={allChecked}
                    columns={columns}
                    refreshDocs={refreshDocs}
                    onTitleClick={handleSharedDocView}
                  />
                )
              })}
              {ready && paginationReady && paginatedData.length === 0 && (
                <div className="no-data">
                  <p className="text-style-3">{t('folder.empty')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="list-view-v2__foot">
        <div className="pagination-v2">
          <a href="/#" className="prev" onClick={handlePrevButtonClick}><span className="material-symbols-outlined weight-300">arrow_back</span></a>
          {Array(allPages).fill().map((_, idx) => {
            return (
              <a href="/#" key={idx} className={`${idx+1 === currentPage ? 'active' : ''}`} onClick={(e) => handleGoToPage(e, idx+1)}>
                {idx+1}
              </a>
            )
          })}
          <a href="/#" className="next" onClick={handleNextButtonClick}><span className="material-symbols-outlined weight-300">arrow_forward</span></a>
        </div>
      </div>

      {showPreviewModal && (
        <SharedDocsCommentsModal 
          onClose={handleClosePreviewModal}
          sharedDocId={sharedDocId}
          sharedDocument={sharedDoc}
          fromListView={true}
        />
      )}
    </div>
  )
}

export default ExternalDocumentsView